import type { LinkColor, LinkLineStyle, LinkMarkStyle } from '@model-framework/link';
import type { FontSize } from '@model-framework/text';
import type { Rect } from '@view-model/models/common/basic/Rect';
import { Point, Size } from '@view-model/models/common/basic';
import type { ThemeColor } from '@view-model/models/common/color';
import type { TextAlign } from '@view-model/models/common/text-align';
import { MenuSize } from '@view-model/models/sticky/ui';
import type { StickyZoneShape } from '@view-model/models/sticky/StickyZoneView';
import { useState } from 'react';
import { MultiSelectionMenuId } from './MultiSelectionMenuId';
import { MultiSelectionMenuItem } from './MultiSelectionMenuItem';
import { MenuContainer } from '@model-framework/menu';
import type { AlignType } from './components';

type MenuGroup = MultiSelectionMenuId[];

/**
 * 選択状態に応じて複数選択メニューのアイテムを決める
 */
export const selectMenuGroups = ({
  isNodeSelected,
  isMultipleNodeSelected,
  isLinkSelected,
  isZoneSelected,
}: {
  isNodeSelected: boolean;
  isMultipleNodeSelected: boolean;
  isLinkSelected: boolean;
  isZoneSelected: boolean;
}): MenuGroup[] => {
  const {
    FontSize,
    FontStrikethrough,
    ThemeColor,
    TextAlign,
    ZoneShape,
    GroupStickyElements,
    LinkLineStyle,
    LinkMarkStyle,
    ReverseLink,
    LinkColor,
    RemoveMultiSelectedLinks,
    HorizontalAlignMenu,
    VerticalAlignMenu,
  } = MultiSelectionMenuId;

  return [
    // 複数選択メニューグループ1（付箋・ゾーン系）
    isNodeSelected || isZoneSelected
      ? [
          FontSize,
          ThemeColor,
          TextAlign,
          ...(isZoneSelected ? [ZoneShape] : []),
          FontStrikethrough,
          GroupStickyElements,
        ]
      : [],
    // 複数選択メニューグループ2（付箋複数選択系）
    isMultipleNodeSelected && !isZoneSelected ? [HorizontalAlignMenu, VerticalAlignMenu] : [],
    // 複数選択メニューグループ3（リンク系）
    isLinkSelected ? [LinkLineStyle, LinkMarkStyle, ReverseLink, LinkColor, RemoveMultiSelectedLinks] : [],
  ];
};

type MenuPosition = {
  menuId: MultiSelectionMenuId;
  position: Point;
};

type MenuLayout = {
  menuPositions: MenuPosition[]; // 各メニューアイテムのポジション
  width: number; // メニュー全体の幅
};

type BorderRectLayout = {
  position: Point; // メニューの枠線の位置
  borderRectWidth: number; // メニューの枠線の幅
};

/**
 * メニューの位置を決める。
 * グループ同士の間はマージンをあける。
 * @param menuGroups
 */
const layoutMenu = (menuGroups: MenuGroup[]): MenuLayout => {
  return menuGroups.reduce(
    ({ menuPositions, width }, group) => {
      // 空のグループは無視
      if (group.length == 0)
        return {
          menuPositions,
          width,
        };

      // 最初のグループはマージンなし
      const margin = width == 0 ? 0 : MenuSize * 0.5;

      group.forEach((menuId, i) => {
        menuPositions.push({ menuId, position: new Point(width + i * MenuSize + margin, 0) });
      });

      return {
        menuPositions,
        width: width + group.length * MenuSize + margin,
      };
    },
    {
      menuPositions: [],
      width: 0,
    } as MenuLayout
  );
};

/**
 * メニューグループの枠線の大きさと位置を決める。
 * グループ同士の間はマージンをあける。
 * @param menuGroups
 */
const getBorderLayouts = (menuGroups: MenuGroup[]): BorderRectLayout[] => {
  const borderRectLayouts = menuGroups
    .map((group, index) => {
      const rectWidth = MenuSize * group.length;
      // 2つ目以降のグループは前のグループの幅とマージンの分だけずらす
      // ただし、現在のindexのグループより前の各グループの長さが全て0の時(リンクのみ選択時など)は、マージン分はずらすことはしない
      const positionX =
        index == 0 || menuGroups.slice(0, index).every((grp) => grp.length <= 0)
          ? 0
          : // 現在のindexのグループより前の各グループに含まれるメニューの総数 * MenuSizeと、
            // 現在のindexより前のグループのうちメニューを１つ以上あるグループの数 * MenuSize/2だけずらしたX座標
            MenuSize *
              menuGroups.slice(0, index).reduce((sum, grp) => {
                return sum + grp.length;
              }, 0) +
            MenuSize * 0.5 * menuGroups.slice(0, index).filter((grp) => grp.length > 0).length;
      return { borderRectWidth: rectWidth, position: new Point(positionX, 0) };
    })
    .filter(({ borderRectWidth }) => borderRectWidth > 0);
  return borderRectLayouts;
};

type Props = {
  selectionRect: Rect;
  isNodeSelected: boolean;
  isMultipleNodeSelected: boolean;
  isLinkSelected: boolean;
  isZoneSelected: boolean;
  currentFontSize?: FontSize;
  currentThemeColor?: ThemeColor;
  currentTextAlign?: TextAlign;
  currentZoneShape?: StickyZoneShape;
  currentLinkLineStyle?: LinkLineStyle;
  currentLinkMarkStyle?: LinkMarkStyle;
  currentLinkColor?: LinkColor;
  onFontSizeSelected(fontSize: FontSize): void;
  onToggleFontStrikethrough(): void;
  onThemeColorSelected(themeColor: ThemeColor): void;
  onTextAlignSelected(textAlign: TextAlign): void;
  onZoneShapeSelected(shape: StickyZoneShape): void;
  onGroupSelectedElements(): void;
  onLinkLineStyleSelected(lineStyle: LinkLineStyle): void;
  onLinkMarkStyleSelected(markStyle: LinkMarkStyle): void;
  onReverseLinks(): void;
  onRemoveLinks(): void;
  onLinkColorSelected(color: LinkColor): void;
  onAlignSelected(alignType: AlignType, interval: number): void;
};

export const MultiSelectionMenu: React.FC<Props> = ({
  selectionRect,
  isNodeSelected,
  isMultipleNodeSelected,
  isLinkSelected,
  isZoneSelected,
  currentFontSize,
  currentThemeColor,
  currentTextAlign,
  currentZoneShape,
  currentLinkLineStyle,
  currentLinkMarkStyle,
  currentLinkColor,
  onFontSizeSelected,
  onToggleFontStrikethrough,
  onThemeColorSelected,
  onTextAlignSelected,
  onZoneShapeSelected,
  onGroupSelectedElements,
  onLinkLineStyleSelected,
  onLinkMarkStyleSelected,
  onReverseLinks,
  onRemoveLinks,
  onLinkColorSelected,
  onAlignSelected,
}: Props) => {
  const [openedMenuId, setOpenedMenuId] = useState<MultiSelectionMenuId | null>(null);

  const menuGroups = selectMenuGroups({
    isNodeSelected,
    isMultipleNodeSelected,
    isLinkSelected,
    isZoneSelected,
  });
  if (menuGroups.length === 0) return null;

  const { menuPositions, width } = layoutMenu(menuGroups);
  const borderLayouts = getBorderLayouts(menuGroups);

  const position = selectionRect.topCenter();
  const menuSize = new Size(width, MenuSize);

  return (
    <MenuContainer position={position} size={menuSize} positionOrigin={'center-bottom'} withoutFrameRect>
      {borderLayouts.map(({ position, borderRectWidth }) => {
        return (
          <rect
            transform={position.toSVGTranslate()}
            key={position.x}
            width={borderRectWidth}
            height={MenuSize}
            rx={2}
            ry={2}
            fill="white"
            stroke="#e0e0e0"
            strokeWidth={4}
          />
        );
      })}

      {menuPositions.map(({ menuId, position }) => {
        return (
          <g key={menuId} transform={position.toSVGTranslate()}>
            <MultiSelectionMenuItem
              menuId={menuId}
              isOpen={openedMenuId === menuId}
              currentFontSize={currentFontSize}
              currentThemeColor={currentThemeColor}
              currentTextAlign={currentTextAlign}
              currentLinkLineStyle={currentLinkLineStyle}
              currentLinkMarkStyle={currentLinkMarkStyle}
              currentLinkColor={currentLinkColor}
              currentZoneShape={currentZoneShape}
              onMenuOpen={() => setOpenedMenuId(menuId)}
              onMenuClose={() => setOpenedMenuId(null)}
              onFontSizeSelected={onFontSizeSelected}
              onToggleFontStrikethrough={onToggleFontStrikethrough}
              onThemeColorSelected={onThemeColorSelected}
              onTextAlignSelected={onTextAlignSelected}
              onZoneShapeSelected={onZoneShapeSelected}
              onGroupSelectedNodes={onGroupSelectedElements}
              onLinkLineStyleSelected={onLinkLineStyleSelected}
              onLinkMarkStyleSelected={onLinkMarkStyleSelected}
              onReverseLinks={onReverseLinks}
              onRemoveLinks={onRemoveLinks}
              onLinkColorSelected={onLinkColorSelected}
              onAlignSelected={onAlignSelected}
              isZoneOnly={isZoneSelected && !isNodeSelected}
            />
          </g>
        );
      })}
    </MenuContainer>
  );
};
